import { Text, Modal, InlineStack, BlockStack, Grid, Form, Divider, FormLayout, TextField, EmptyState, Button, Select } from '@shopify/polaris';
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import helpers from 'helpers/index';
import { PickupMeta, ENUM_ORDER_FEE, ORDER_FEE_LABEL, useMutateOrderBooking } from 'queries/orders.query';
import { ConfettiIcon, DuplicateIcon, PlusIcon } from '@shopify/polaris-icons';
import { useNotification } from 'context/NotificationContext';
import __helpers from 'helpers/index';
import { lengthLessThan, notEmpty, useField, useForm } from '@shopify/react-form';
import __ from 'languages/index';
import { TypedUser } from 'interface/user.model';
import { TypedTripData } from 'queries/trip.query';
import { useGetJourney } from 'queries/journey.query';
import { useMutateSearchUser } from 'queries/users.query';
import { ENUM_TICKET_TYPE } from 'queries/ticket.query';

function takeNumberOnly(input: string): string {
  return input.replace(/\D/g, '');
}

export default function AddTicketModal({
  show,
  seat_position,
  trip_manifest_id,
  tripData,
  currentTicketType = ENUM_TICKET_TYPE.PASSENGER, // mặc định
  onClose,
}: {
  show: boolean;
  seat_position: number[];
  trip_manifest_id?: string;
  tripData: TypedTripData;
  currentTicketType: string;
  onClose: () => void;
}) {
  const StringQuery = useMemo(() => {
    const rootSearchPath = window.location.search;
    return helpers.ExtractUrl(rootSearchPath);
  }, [window.location.search]);

  const { addNotification } = useNotification();
  const { mutateAsync: bookAticket, isSuccess: createSuccesfully } = useMutateOrderBooking();

  // debug

  const [currentCustomerData, setCurrentCustomerData] = useState<TypedUser>(null);
  const loadCustomerFromLocal = useCallback(() => {
    try {
      let quickbook_customer_data = sessionStorage.getItem('quickbook_customer_data');
      let quickbook_customer_data_after_parse = JSON.parse(quickbook_customer_data);
      setCurrentCustomerData(quickbook_customer_data_after_parse);
      if (quickbook_customer_data_after_parse && quickbook_customer_data_after_parse.user_phonenumber) {
        useFields.customer_phone.onChange(quickbook_customer_data_after_parse.user_phonenumber);
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    loadCustomerFromLocal();
  }, []);

  /** QUICK BOOK */
  /** QUICK BOOK */
  /** QUICK BOOK */
  /** QUICK BOOK */

  const useFields = {
    /** create new one */
    trip_time: useField<string>({ value: '', validates: [] }),
    trip_date: useField<string>({ value: '', validates: [] }),

    /** Edit ... */
    customer_name: useField<string>({
      value: currentCustomerData?.display_name ?? '',
      validates: [notEmpty('Không được để trống.')],
    }),
    customer_phone: useField<string>({
      value: '',
      validates: [
        notEmpty('Không được để trống.'),
        (inputVal) => {
          if (!helpers.isPhoneNumber(inputVal)) return 'Phải là một số điện thoại.';
        },
      ],
    }),
    pickup_address: useField<string>({ value: '', validates: [notEmpty('Trường này không được để trống.')] }),
    pickup_time: useField<string>({
      value: tripData?.time,
      validates: [
        (inputVal) => {
          if (inputVal && !helpers.isValidTime(inputVal)) return 'Giờ định dạng sai (hh:mm)';
        },
      ],
    }),
    pickup_place_id: useField<string>({ value: '', validates: [] }),

    pickup_province: useField<string>({ value: '', validates: [] }),
    pickup_district: useField<string>({ value: '', validates: [] }),
    pickup_ward: useField<string>({ value: '', validates: [] }),

    pickup_note: useField<string>({ value: '', validates: [] }),

    /** Case: Đặt hộ */
    pickup_contact_name: useField<string>({ value: '', validates: [] }),
    pickup_contact_phonenumber: useField<string>({
      value: '',
      validates: [
        (inputVal) => {
          if (inputVal && !helpers.isPhoneNumber(inputVal)) return 'Số điện thoại không hợp lệ';
        },
      ],
    }),

    dropoff_address: useField<string>({
      value: '',
      validates: [notEmpty('Trường này không được để trống.')],
    }),
    dropoff_contact_name: useField<string>({
      value: '',
      validates: [],
    }),
    dropoff_contact_phonenumber: useField<string>({
      value: '',
      validates: [],
    }),

    dropoff_place_id: useField<string>({ value: '', validates: [] }),

    dropoff_province: useField<string>({ value: '', validates: [] }),
    dropoff_district: useField<string>({ value: '', validates: [] }),
    dropoff_ward: useField<string>({ value: '', validates: [] }),

    dropoff_note: useField<string>({ value: '', validates: [] }),

    note: useField<string>({ value: '', validates: [lengthLessThan(1000, 'Ghi chú quá dài...')] }),
    order_code: useField<string>({ value: '', validates: [lengthLessThan(9, 'Mã vé quá dài...')] }),

    /** Lưu ý: Nếu có pricetable thì? */
    // price: useField<number>({
    //   value: tripData?.price || 0,
    //   validates: [
    //     (inputVal) => {
    //       if (!helpers.isNumber(inputVal)) return 'Phải là một số hợp lệ.';
    //     },
    //   ],
    // }),
    // discount: useField<number>({
    //   value: 0,
    //   validates: [
    //     (inputVal) => {
    //       if (!helpers.isNumber(inputVal)) return 'Phải là một số hợp lệ.';
    //       if (Number(inputVal) < 0) return 'Phải là một số dương hợp lệ.';
    //     },
    //   ],
    // }),
    total_fee: useField<number>({
      value: 0,
      validates: [],
    }),

    price_table: useField<{ price: number; discount: number; ticket_type: string; seat_position: string }[]>({
      value: [{ price: 0, discount: 0, ticket_type: String(currentTicketType).toUpperCase(), seat_position: 'trunk' }],
      validates: [],
    }),

    order_fee: useField<{ fee_value: number; fee_description: string }[]>({
      value: [],
      validates: [],
    }),

    payment_status: useField<number>({ value: 0, validates: [] }), // read only
    payment_method: useField<string>({ value: 'CASH', validates: [] }), //
    casher: useField<string | undefined>({ value: undefined, validates: [] }), // read only
    casher_name: useField<string>({ value: '', validates: [] }), // read only, show only

    user_address_book_meta_pickup: useField<PickupMeta>({
      value: {
        extra_price: 0,
        minute: 0,
        note: '',
        place_id: '',
      },
      validates: [],
    }), // read only
    user_address_book_meta_dropoff: useField<PickupMeta>({
      value: {
        extra_price: 0,
        minute: 0,
        note: '',
        place_id: '',
      },
      validates: [],
    }), // read only
  };

  useEffect(() => {
    if (useFields.payment_status.value === 0) {
      useFields.casher.onChange(undefined);
      useFields.casher_name.onChange('');
    }
  }, [useFields.payment_status.value]);

  /** dành để edit field */
  const { data: journeyData } = useGetJourney(StringQuery.journey_id);

  const { fields, submit, submitting, dirty, makeClean, reset } = useForm({
    fields: useFields,
    async onSubmit(values) {
      try {
        let bookedTicketData = await bookAticket({
          journey_id: StringQuery.journey_id,
          customer_name: values.customer_name,
          customer_phone: values.customer_phone,
          pickup_time: values.pickup_time,
          pickup_address: values.pickup_address,

          dropoff_address: values.dropoff_address,

          order_code: values.order_code,
          note: values.note,
          // price: values.price,
          // discount: values.discount,
          price_table: values.price_table,
          order_fee: values.order_fee,

          departure_date: new Date(values.trip_date + ' ' + values.trip_time).getTime(),
          // seat_quantity: values.seat_quantity,
          trip_manifest_id: trip_manifest_id,

          user_address_book_meta_pickup: values.user_address_book_meta_pickup,
          user_address_book_meta_dropoff: values.user_address_book_meta_dropoff,
        });
        let allTicketIDs = bookedTicketData.map((al) => al.id);
        addNotification('info', `Tổng ${allTicketIDs.length} vé đã được tạo.`);
        // if (trip_manifest_id) await submitTicketIDTOTripManifest(allTicketIDs);
      } catch (e: any) {
        if (e.params !== undefined) {
          if (e.params.field !== undefined) {
            useFields[e.params.field].setError(__('check_this_field_again'));
          }
        }
      }
      return { status: 'success', errors: [] };
    },
  });

  /** reset lai form */
  useEffect(() => {
    if (show === false) {
      helpers.resetForm(useFields);
      makeClean();
    }
  }, [show]);

  // ... sau khi khai báo useForm

  useEffect(() => {
    // 1. Chỉ chạy khi có ticketData và có departure_date
    if (StringQuery.d) {
      try {
        // 2. Parse chuỗi ngày giờ từ server

        // 3. Format thành 2 phần riêng biệt
        const timePart = StringQuery.htext;
        const datePart = StringQuery.d;

        // 4. Dùng hàm onChange của field để cập nhật giá trị mới cho form
        fields.trip_time.onChange(timePart);
        fields.trip_date.onChange(datePart);
      } catch (error) {
        console.error('Lỗi khi định dạng ngày giờ:', error);
        // Có thể reset field về rỗng nếu ngày giờ lỗi
        fields.trip_time.onChange('');
        fields.trip_date.onChange('');
      }
    }
  }, [StringQuery]);

  useEffect(() => {
    if (createSuccesfully) {
      sessionStorage.removeItem('quickbook_customer_data');
      onClose();
    }
  }, [createSuccesfully]);

  /** tính toán tổng phải thanh toán */
  const [totalMustPay, setTotalMustPay] = useState<number>(0);
  useEffect(() => {
    const fee = Number(fields.total_fee.value || 0);
    let totalMustPay = 0;

    let allPriceTable = useFields.price_table.value;
    for (let priceTag of allPriceTable) {
      totalMustPay += priceTag.price - priceTag.discount;
    }
    setTotalMustPay(totalMustPay + fee);
  }, [fields.price_table.value, fields.total_fee.value]);

  useEffect(() => {
    if (helpers.isEmpty(useFields.order_fee.value)) {
      useFields.total_fee.onChange(0);
      return;
    }
    let total_fee = 0;
    for (let d of useFields.order_fee.value) {
      total_fee += d.fee_value;
    }
    useFields.total_fee.onChange(total_fee);
  }, [useFields.order_fee.value]);

  const fromClipboard = useCallback(async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      fields.customer_phone.onChange(takeNumberOnly(clipboardText));
    } catch (e) {}
  }, []);

  const { mutateAsync: searchUser } = useMutateSearchUser();
  const loadCustomerDataSuggestion = useCallback(async (phonenumber: string) => {
    try {
      let customerData = await searchUser({
        user_phonenumber: phonenumber,
      });
      if (!helpers.isEmpty(customerData)) {
        setCurrentCustomerData(customerData[0]);
      }
    } catch (e) {}
  }, []);

  const lastSearchedPhone = useRef<string>('');
  const searchCustomerDebounce = useMemo(
    () =>
      helpers.debounce((value: string) => {
        if (!value || value === lastSearchedPhone.current) return;
        lastSearchedPhone.current = value;
        loadCustomerDataSuggestion(value);
      }, 400),
    [loadCustomerDataSuggestion]
  );

  useEffect(() => {
    const normalizedPhone = takeNumberOnly(fields.customer_phone.value);
    if (helpers.isPhoneNumber(normalizedPhone)) {
      searchCustomerDebounce(normalizedPhone);
    } else {
      lastSearchedPhone.current = '';
    }
  }, [fields.customer_phone.value, searchCustomerDebounce]);

  /** set gía khi edit */
  useEffect(() => {
    if (!seat_position) return;
    let is_shipment = currentTicketType === ENUM_TICKET_TYPE.SHIPMENT;
    let price_table = [];
    for (let seat of seat_position) {
      if (is_shipment)
        price_table.push({
          price: Number(tripData?.shipping_price || 0),
          discount: 0,
          ticket_type: String(currentTicketType).toUpperCase(),
          seat_position: seat,
        });
      else
        price_table.push({
          price: Number(tripData?.price || 0),
          discount: 0,
          ticket_type: String(currentTicketType).toUpperCase(),
          seat_position: seat,
        });
    }
    useFields.price_table.onChange(price_table);
  }, [seat_position, tripData, currentTicketType]);

  /** Show form thêm thông tin đón */
  const [showPickUpContactInformation, setShowPickUpContactInformation] = useState(false);

  /** Thêm phí */
  const handleAddOrderFee = useCallback(() => {
    useFields.order_fee.onChange([...useFields.order_fee.value, { fee_description: 'PICKUP_FEE', fee_value: 0 }]);
  }, [useFields.order_fee.value]);

  // đặt gần các hook khác
  const handlePriceChange = useCallback(
    (raw: string, index: number) => {
      const newPrice = __helpers.parseNumeric(raw);
      const list = [...useFields.price_table.value];

      if (index === 0) {
        // cập nhật đồng loạt tất cả vé theo giá của vé #1
        const updated = list.map((it) => ({ ...it, price: newPrice }));
        useFields.price_table.onChange(updated);
      } else {
        // chỉ cập nhật vé đang sửa
        list[index] = { ...list[index], price: newPrice };
        useFields.price_table.onChange(list);
      }
    },
    [useFields.price_table.value]
  );

  // Nuts copy
  const copyReceiverFromSenderCallback = useCallback(() => {
    useFields.dropoff_contact_name.onChange(useFields.customer_name.value);
    useFields.dropoff_contact_phonenumber.onChange(useFields.customer_phone.value);
  }, [useFields.customer_name.value, useFields.customer_phone.value]);

  return (
    <>
      <Modal
        size="large"
        open={show}
        title={`Đặt vé ${currentTicketType === ENUM_TICKET_TYPE.SHIPMENT ? 'hàng' : 'khách'} cho chuyến ${StringQuery.htext || '?'} ngày ${StringQuery.d ?? 'chưa rõ'}`}
        onClose={() => onClose()}
        primaryAction={{
          content: `Đặt`,
          icon: ConfettiIcon,
          disabled: !dirty || totalMustPay < 1,
          loading: submitting,
          onAction: submit,
        }}
      >
        <Modal.Section>
          {!StringQuery.htext ? (
            <EmptyState
              image={''}
              heading="Chưa chọn chuyến nào"
              footerContent={<Text as="p">Chọn vào một chuyến trước rồi bấm nút đặt lại.</Text>}
            />
          ) : (
            <Form onSubmit={submit}>
              <Grid columns={{ xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }}>
                <Grid.Cell>
                  <BlockStack gap={{ xs: '200', sm: '400' }}>
                    <FormLayout.Group>
                      <TextField
                        label="SĐT"
                        placeholder="Số điện thoại"
                        autoComplete="off"
                        type="tel"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        disabled={submitting}
                        clearButton
                        value={fields.customer_phone.value}
                        onChange={(v) => {
                          fields.customer_phone.onChange(takeNumberOnly(v));
                          // searchCustomerDebound();
                        }}
                        // onBlur={() => loadCustomerDataSuggestion()}
                        error={fields.customer_phone.error}
                        onClearButtonClick={() => {
                          fields.customer_phone.onChange('');
                          setCurrentCustomerData(null);
                        }}
                        labelAction={{
                          content: 'Dán',
                          onAction: () => {
                            fromClipboard();
                          },
                        }}
                      />

                      <TextField
                        autoComplete="off"
                        placeholder="Tên khách hàng"
                        label="Tên"
                        {...fields.customer_name}
                        onChange={(e) => fields.customer_name.onChange(helpers.capitalizeWords(e))}
                        disabled={submitting}
                        clearButton
                        onClearButtonClick={() => fields.customer_name.onChange('')}
                      />
                    </FormLayout.Group>
                    <BlockStack gap="200">
                      <div>
                        <Button variant="plain" icon={PlusIcon} onClick={() => setShowPickUpContactInformation((old) => !old)}>
                          Đặt hộ người khác?
                        </Button>
                      </div>

                      {showPickUpContactInformation ? (
                        <FormLayout.Group>
                          <TextField
                            label=""
                            placeholder="Số điện thoại"
                            autoComplete="off"
                            type="tel"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            disabled={submitting}
                            clearButton
                            value={fields.pickup_contact_phonenumber.value}
                            onChange={(v) => {
                              fields.pickup_contact_phonenumber.onChange(String(v).trim());
                              // searchCustomerDebound();
                            }}
                            // onBlur={() => loadCustomerDataSuggestion()}
                            error={fields.pickup_contact_phonenumber.error}
                            onClearButtonClick={() => {
                              fields.pickup_contact_phonenumber.onChange('');
                              setCurrentCustomerData(null);
                            }}
                          />

                          <TextField
                            autoComplete="off"
                            placeholder="Tên khách hàng"
                            label=""
                            {...fields.pickup_contact_name}
                            onChange={(e) => fields.pickup_contact_name.onChange(helpers.capitalizeWords(e))}
                            disabled={submitting}
                            clearButton
                            onClearButtonClick={() => fields.pickup_contact_name.onChange('')}
                          />
                        </FormLayout.Group>
                      ) : null}
                    </BlockStack>
                    <Divider />

                    <TextField
                      autoComplete="off"
                      placeholder="Địa chỉ"
                      label={`Đón tại ` + journeyData?.journey_from}
                      disabled={submitting}
                      {...useFields.pickup_address}
                    />

                    <TextField
                      autoComplete="off"
                      placeholder="Địa chỉ"
                      label={`Xuống tại ` + journeyData?.journey_to}
                      disabled={submitting}
                      {...useFields.dropoff_address}
                    />

                    {currentTicketType && currentTicketType === ENUM_TICKET_TYPE.SHIPMENT && (
                      <BlockStack gap="400" inlineAlign="start">
                        <Divider />
                        <Text as="h3" variant="headingMd">
                          Thông tin người nhận hàng
                        </Text>
                        <FormLayout.Group condensed>
                          <TextField
                            autoComplete="off"
                            label={`Tên người nhận hàng`}
                            {...fields.dropoff_contact_name}
                            requiredIndicator
                            disabled={submitting}
                          />
                          <TextField
                            autoComplete="off"
                            label={`Số liên lạc`}
                            {...fields.dropoff_contact_phonenumber}
                            requiredIndicator
                            disabled={submitting}
                          />
                        </FormLayout.Group>
                        <Button
                          icon={DuplicateIcon}
                          variant="plain"
                          onClick={() => {
                            copyReceiverFromSenderCallback();
                          }}
                        >
                          Copy thông tin từ người đặt
                        </Button>
                      </BlockStack>
                    )}
                  </BlockStack>
                </Grid.Cell>
                <Grid.Cell>
                  <BlockStack gap={{ xs: '200', sm: '400' }}>
                    <TextField autoComplete="off" label="Mã vé" {...fields.order_code} disabled={submitting} />

                    <TextField
                      autoComplete="off"
                      multiline={2}
                      label="Ghi chú"
                      showCharacterCount
                      maxLength={1000}
                      {...fields.note}
                      disabled={submitting}
                    />

                    <Text as="h3" variant="headingMd">
                      Các chi phí
                    </Text>
                    {useFields.order_fee.value?.map((feeData, index) => {
                      return (
                        <FormLayout.Group key={`order_fee_${index}`}>
                          <Select
                            label="Chọn:"
                            labelInline
                            options={helpers.enumToArray(ENUM_ORDER_FEE).map((el) => {
                              return {
                                label: ORDER_FEE_LABEL[el],
                                value: el,
                              };
                            })}
                            onChange={(a) => {
                              const updatedPriceTable = [...useFields.order_fee.value];
                              updatedPriceTable[index] = { ...updatedPriceTable[index], fee_description: a };
                              useFields.order_fee.onChange(updatedPriceTable);
                            }}
                            value={feeData.fee_description}
                          />
                          <TextField
                            autoComplete="off"
                            label={``}
                            type="tel"
                            placeholder="Số tiền"
                            inputMode="numeric"
                            pattern="[0-9.,]*"
                            value={__helpers.formatNumber(feeData.fee_value)}
                            onChange={(a) => {
                              const updatedPriceTable = [...useFields.order_fee.value];
                              updatedPriceTable[index] = { ...updatedPriceTable[index], fee_value: __helpers.parseNumeric(a) };
                              useFields.order_fee.onChange(updatedPriceTable);
                            }}
                            disabled={submitting}
                          />
                        </FormLayout.Group>
                      );
                    })}
                    <div>
                      <Button icon={PlusIcon} variant="plain" onClick={handleAddOrderFee}>
                        Thêm phí
                      </Button>
                    </div>

                    <Text as="h3" variant="headingMd">
                      Giá vé
                    </Text>

                    {useFields.price_table.value.map((el, index) => {
                      return (
                        <FormLayout.Group condensed key={`price_${index}_${el.seat_position}`}>
                          <TextField
                            autoComplete="off"
                            label={`Giá vé #${index + 1}`}
                            type="tel"
                            inputMode="numeric"
                            pattern="[0-9.,]*"
                            value={__helpers.formatNumber(el.price)}
                            onChange={(a) => handlePriceChange(a, index)} // ⬅️ dùng handler
                            disabled={submitting}
                          />
                          <TextField
                            autoComplete="off"
                            label="Giảm giá"
                            type="tel"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={__helpers.formatNumber(el.discount)}
                            // onChange={(a) => {
                            //   fields.discount.onChange(helpers.parseNumeric(a));
                            // }}
                            onChange={(a) => {
                              const updatedPriceTable = [...useFields.price_table.value];
                              updatedPriceTable[index] = { ...updatedPriceTable[index], discount: __helpers.parseNumeric(a) };
                              useFields.price_table.onChange(updatedPriceTable);
                            }}
                            disabled={submitting}
                          />
                        </FormLayout.Group>
                      );
                    })}

                    <Divider />

                    <InlineStack align="space-between" blockAlign="center">
                      <Text as="span" fontWeight="bold">
                        Số chỗ
                      </Text>
                      <Text as="h3" fontWeight="bold" variant="headingMd" tone="critical">
                        {__helpers.formatNumber(seat_position.length)} chỗ
                      </Text>
                    </InlineStack>
                    <InlineStack align="space-between" blockAlign="center">
                      <Text as="span" fontWeight="bold">
                        Giá vé
                      </Text>
                      <Text as="h3" fontWeight="bold" variant="headingMd" tone="critical">
                        {__helpers.formatNumber(tripData?.price)}
                      </Text>
                    </InlineStack>
                    <InlineStack align="space-between" blockAlign="center">
                      <Text as="span" fontWeight="bold">
                        Tổng phí thêm
                      </Text>
                      <Text as="h3" fontWeight="bold" variant="headingMd" tone="critical">
                        {__helpers.formatNumber(useFields.total_fee.value)}
                      </Text>
                    </InlineStack>
                    <InlineStack align="space-between" blockAlign="center">
                      <Text as="span" fontWeight="bold">
                        Tổng phải thanh toán
                      </Text>
                      <Text as="h3" fontWeight="bold" variant="headingLg" tone="critical">
                        {__helpers.formatNumber(totalMustPay)}
                      </Text>
                    </InlineStack>
                  </BlockStack>
                </Grid.Cell>
              </Grid>
            </Form>
          )}
        </Modal.Section>
      </Modal>
    </>
  );
}

